import type { SerialNumber } from "~domain/devices/models/common-device-model.ts";
import type { ModulatorLinkCsId } from "~domain/devices/models/links/link-model.ts";
import type { ModulatorCsId, ModulatorHwId } from "~domain/devices/models/modulators/modulator-model.ts";

import { z } from "zod";

export const autoDiagDeviceDtoSchema = z.object({
  info: z
    .object({
      canDiagnose: z.boolean(),
      serial: z
        .string()
        .transform((s) => s as SerialNumber)
        .nullable(),
      myVName: z.string().nullable(),
      technicalName: z.string().nullable(),
      csLinkId: z.number().transform((s) => s as ModulatorLinkCsId),
      hwId: z.number().transform((s) => s as ModulatorHwId),
      hasEnergyManager: z.enum(["HAS_ENERGY_MANAGER", "NO_ENERGY_MANAGER", "ERROR_INFRA_VOLTALIS"]).nullable(),
    })
    .nullable(),
  diagnostic: z
    .object({
      start: z.coerce.date().nullable(),
      end: z.coerce.date().nullable(),
      result: z
        .enum(["OK", "NOK_NO_POWER", "NOK_NO_MODULATION", "NOK_UNREACHABLE", "NOK_DEVICE_UNKNOWN", "FAILED_BACKEND"])
        .nullable(),
    })
    .nullable(),
  programming: z
    .object({
      mode: z
        .enum([
          "NORMAL",
          "CONFORT",
          "CONFORTMINUS1",
          "CONFORTMINUS2",
          "ECO",
          "ECOV",
          "HORS_GEL",
          "TEMPERATURE",
          "ARRET",
          "UNKNOWN",
          "ERROR",
        ])
        .nullable(),
      type: z.enum(["DEFAULT", "USER", "QUICK", "MANUAL", "UNKNOWN", "ERROR"]).nullable(),
      failedToFetchMyVProgramming: z.boolean(),
      conform: z.enum(["CONFORM", "NOT_CONFORM", "FAILED"]),
    })
    .nullable(),
  memo: z
    .object({
      content: z.nullable(z.string()).catch(""),
      actions: z.array(z.enum(["SYNCHRONISATION", "SYNCHRONIZE_MYV_PROGRAM"])),
    })
    .nullable(),
  sensor: z
    .object({
      csId: z.number().transform((s) => s as ModulatorCsId),
      hwId: z.number().transform((s) => s as ModulatorHwId),
      installationDatetime: z.coerce.date(),
      serial: z
        .string()
        .transform((s) => s as SerialNumber)
        .nullable(),
    })
    .nullable(),
});

export const backofficeFindAutodiagDiagnosisResponseSchema = z.object({
  lastSync: z.coerce.date().nullable(),
  devices: z.array(autoDiagDeviceDtoSchema),
  failures: z
    .object({
      failedToFetchMyVEnergyManager: z.boolean(),
    })
    .nullable(),
  disableAutoDiagFeature: z.boolean(),
});

export type BackofficeFindAutodiagDiagnosisResponse = z.infer<typeof backofficeFindAutodiagDiagnosisResponseSchema>;
export type BackofficeAutoDiagDevice = z.infer<typeof autoDiagDeviceDtoSchema>;
